import { useCallback, useState } from 'react'
import {
  CreateAgreementFormPacketModel,
  CreateAgreementTemplateDetails,
  CreateAgreementTemplateDetailsError,
} from '@user/components/Agreements/AgreementFormPacketDialog/typings'
import { useSelector } from 'react-redux'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'
import { useLeadOrOppDetails } from '@user/components/Agreements/AgreementFormPacketDialog/hooks/useLeadOrOppDetails'
import {
  AddFormPacketRequest,
  AgreementSource,
  createFormPacket,
  RenderObjectType,
  selectAgreementConfigs,
  updateFormPacket,
} from '@user/store/agreements'
import { updateLeadAgreementsCount } from '@user/store/lead'
import { updateOpportunityAgreementsCount } from '@user/store/opportunity'
import { toast } from '@sales-center/components'
import { useAppDispatch } from '@user/store/hooks'
import { getAuthHeader } from '@user/store/services/crud'
import { ServiceConfig } from '@user/store/services/config'

import { ENTITY_TYPES, updateLoadingState } from '@sales-center/common'

interface CreateAgreementFormPacketErrorModel {
  templates?: CreateAgreementTemplateDetailsError[]
}

export function useActions(
  entityId: number,
  entityType: ENTITY_TYPES,
  onClose: () => void,
  source: AgreementSource = 'detail',
) {
  const services = useSelector(selectAllLeadOppProducts)
  const details = useLeadOrOppDetails(entityType)
  const [formId, setFormId] = useState<number>()
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const dispatch = useAppDispatch()
  const baseUrl = `${ServiceConfig.baseAPIUrl}/Proposals`
  const openEditDrawer = useCallback(() => setEditDrawerOpen(true), [])
  const agreementConfigs = useSelector(selectAgreementConfigs)

  const closeEditDrawer = useCallback(() => {
    setEditDrawerOpen(false)
    onClose()
  }, [onClose])

  const onValidate = useCallback(
    (model: CreateAgreementFormPacketModel) => {
      const errors: CreateAgreementFormPacketErrorModel = {}

      const { templates } = model
      errors.templates = []

      templates.map((template: CreateAgreementTemplateDetails, index) => {
        if ('templateId' in template) {
          const { dynamicServiceBlock, numOfServices } = template

          const templateErrors: CreateAgreementTemplateDetailsError = {}
          if (!template.templateId || template.templateId < 1) {
            templateErrors.index = index
            templateErrors.templateId = 'Template is required'
            errors.templates?.push(templateErrors)
          } else {
            if (!template.services || template.services?.length === 0) {
              templateErrors.index = index
              templateErrors.templateId = 'required'
              templateErrors.servicesEmpty = true
            }

            if (!dynamicServiceBlock) {
              const distinctSvcLocs = new Set(
                services.map((s) => s.serviceLocationId),
              )

              if (
                Number(template.services?.length || -1) > Number(numOfServices)
              ) {
                templateErrors.index = index
                templateErrors.templateId = 'required'
                templateErrors.exceedsServiceSlots = true
              }

              if (distinctSvcLocs.size > 1) {
                templateErrors.index = index
                templateErrors.templateId = 'required'
                templateErrors.exceedsServiceLocations = true
              }
            }

            if (
              template?.isCardCaptureEnabled &&
              details?.externalLocationCode == null
            ) {
              templateErrors.index = index
              templateErrors.templateId = 'required'
              templateErrors.showPestPacLocationError = true
            }

            if (
              templateErrors.exceedsServiceSlots ||
              templateErrors.showPestPacLocationError ||
              templateErrors.includeDetail ||
              templateErrors.servicesEmpty ||
              templateErrors.exceedsServiceLocations
            ) {
              errors.templates?.push(templateErrors)
            }
          }
        }
      })

      if (errors.templates.length === 0) {
        return {}
      }

      return errors
    },
    [details?.externalLocationCode, services],
  )

  const createFormPacketData = useCallback(
    (model: CreateAgreementFormPacketModel) => {
      const templates = model.templates?.filter(
        (item) => 'templateId' in item,
      ) as CreateAgreementTemplateDetails[]
      const requestData: AddFormPacketRequest = {
        externalEntityType1: details?.externalLocationId ? 'Location' : null,
        externalEntityId1: details?.externalLocationId || null,
        externalEntityType5: model.entityType,
        externalEntityId5: model.entityId,
        templateList: templates.map((template) => template.templateId),
        templateServices: templates.map((template) => {
          return {
            templateId: template.templateId,
            dynamicServiceBlockMetadata: {
              includeDetail: template.includeDetail || false,
              serviceIds: template.services
                ? template.services.map((item) => item.id)
                : [],
            },
          }
        }),
      }

      requestData.agreementRenderObjects = model.templates?.map((row) => {
        if ('templateId' in row) {
          return {
            renderObjectType: RenderObjectType.Template,
            isFileFromCoreApp: false,
            id: String(row?.templateId),
            name: row?.templateFormName,
            fileSize: null,
            fileCreatedDate: null,
            isCompletedFormPdf: false,
          }
        }
        return {
          renderObjectType: RenderObjectType.Attachment,
          isFileFromCoreApp: !row?.isSaleCenterAttachment,
          id: row?.id,
          name: row?.displayName,
          fileSize: row?.fileSize || null,
          fileCreatedDate: row?.createdDate,
          isCompletedFormPdf: row?.isCompletedFormPdf,
        }
      })

      if (templates.length === 1) {
        const template = templates[0]
        requestData.templateId = template.templateId
        requestData.formName = template.templateFormName
        requestData.appData = [
          { key: 'includeDetail', value: Boolean(template.includeDetail) },
        ]
      } else {
        requestData.packetName = ''
        requestData.IsPacket = true
      }

      if (agreementConfigs) {
        requestData.packetId = agreementConfigs.packetId
        requestData.formId = agreementConfigs.formId
      }

      requestData.source = source

      return requestData
    },
    [agreementConfigs, details?.externalLocationId, source],
  )

  const onSubmit = useCallback(
    async (model: CreateAgreementFormPacketModel) => {
      const requestData = createFormPacketData(model)
      const isSaveForLater = sessionStorage.getItem('SaveForLater')

      dispatch(updateLoadingState('visible'))
      const apiCall = agreementConfigs ? updateFormPacket : createFormPacket

      const response = await dispatch(apiCall(requestData))

      if (apiCall.fulfilled.match(response)) {
        dispatch(updateLoadingState('hidden'))
        const payload = response.payload

        if (payload.status == 'success') {
          if (!isSaveForLater) {
            setFormId(payload.formId || agreementConfigs?.formId)
          }

          if (!agreementConfigs) {
            const action =
              entityType == ENTITY_TYPES.Lead
                ? updateLeadAgreementsCount
                : updateOpportunityAgreementsCount
            dispatch(
              action({
                action: 'increment',
                leadId: entityId,
                oppId: entityId,
              }),
            )
          }

          if (isSaveForLater) {
            sessionStorage.removeItem('SaveForLater')
            closeEditDrawer()
          } else {
            openEditDrawer()
          }
        }

        if (payload.status == 'failed') {
          toast.error(payload.statusMessage)
        }
      } else {
        dispatch(updateLoadingState('hidden'))
        toast.error(
          `Failed to add agreement to the selected ${entityType.toLowerCase()}.`,
        )
      }
    },
    [
      createFormPacketData,
      dispatch,
      agreementConfigs,
      entityType,
      entityId,
      closeEditDrawer,
      openEditDrawer,
    ],
  )

  const onPreview = useCallback(
    async (model: CreateAgreementFormPacketModel) => {
      const requestData = createFormPacketData(model)

      dispatch(updateLoadingState('visible'))
      fetch(`${baseUrl}/form/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeader(),
        },
        credentials: 'include',
        body: JSON.stringify(requestData),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.blob()
        })
        .then((blob) => {
          dispatch(updateLoadingState('hidden'))
          const blobUrl = URL.createObjectURL(blob)
          window.open(blobUrl, '_blank')
        })
        .catch(() => {
          dispatch(updateLoadingState('hidden'))
          toast.error(`Failed to preview agreement.`)
        })
    },
    [baseUrl, createFormPacketData, dispatch],
  )

  return {
    onValidate,
    onSubmit,
    formId,
    editDrawerOpen,
    closeEditDrawer,
    onPreview,
  }
}
